import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/services/authApi'
import type { UserInfo } from '@/models/Auth'

const AUTH_CONFIG_TTL = 5 * 60 * 1000

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('token'))
  const user = ref<UserInfo | null>(null)

  const appName = ref(import.meta.env.VITE_APP_NAME || 'StudyQuiz')
  const authEnabled = ref(true)
  const authType = ref('credentials')
  const setupRequired = ref(false)
  const userRegistrationEnabled = ref(true)
  const configFetchedAt = ref<number | null>(null)

  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(
    () => user.value?.roles?.some((r) => r.toLowerCase() === 'admin') ?? false,
  )

  async function login(email: string, password: string): Promise<boolean> {
    try {
      const { data } = await authApi.login({ username: email, password })
      token.value = data.access_token
      localStorage.setItem('token', data.access_token)
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token)
      }
      await fetchUserInfo()
      return true
    } catch {
      return false
    }
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refresh_token')
    configFetchedAt.value = null
  }

  async function fetchUserInfo() {
    if (!token.value) return
    try {
      const { data } = await authApi.getCurrentUser()
      user.value = data
    } catch {
      logout()
    }
  }

  async function checkAuthConfig(force = false) {
    const now = Date.now()
    if (!force && configFetchedAt.value && now - configFetchedAt.value < AUTH_CONFIG_TTL) {
      return
    }
    try {
      const { data } = await authApi.getAuthConfig()
      appName.value = data.app_name
      authEnabled.value = data.auth_enabled
      authType.value = data.auth_type
      setupRequired.value = data.setup_required
      userRegistrationEnabled.value = data.user_registration_enabled
      configFetchedAt.value = now
    } catch {
      // Leave defaults in place if the backend is unreachable.
    }
  }

  if (token.value) {
    fetchUserInfo()
  }

  return {
    token,
    user,
    appName,
    authEnabled,
    authType,
    setupRequired,
    userRegistrationEnabled,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    fetchUserInfo,
    checkAuthConfig,
  }
})
